import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { pinDmTranslationLinks } from '@arthelper/shared/dm-translation-links';
import {
  dmTranslateClickEventForResult,
  dmTranslateMenuPending,
  initialDmTranslateClick,
  reduceDmTranslateClick,
  type DmTranslateClickEvent,
} from '@arthelper/shared/dm-translate-click'; // pragma: allowlist secret
import { SUPPORTED_LOCALES, LOCALE_DISPLAY_NAMES, isLocale, resolveTranslation, type Locale } from '@arthelper/shared';
import { linkifyPlainUrls, parseMessageContent } from '~/utils/parseMessageContent';
import { TRANSLATE_MESSAGE_PART } from '~/graphql/direct-messages';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

interface TranslationRow {
  locale: string;
  content: string;
  status: string;
}

interface TranslatableMessageTextProps {
  content: string;
  sourceLocale?: string | null;
  translations?: TranslationRow[] | null;
  /** The viewer's own message — a blue bubble, so links need the on-dark variant. */
  isSelf: boolean;
  messagePartId?: string;
  threadId?: string;
}

const CHOICE_PREFIX = 'dm-translate-locale:';

function rememberChoice(threadId: string | undefined, locale: string) {
  if (!threadId) return;
  try {
    localStorage.setItem(`${CHOICE_PREFIX}${threadId}`, locale);
  } catch {
    // Private mode and disabled storage still show the translation for this view.
  }
}

function storedChoice(threadId: string | undefined): string | null {
  if (!threadId) return null;
  try {
    return localStorage.getItem(`${CHOICE_PREFIX}${threadId}`);
  } catch {
    return null;
  }
}

function isReady(row: TranslationRow): boolean {
  return row.status === 'completed' || row.status === 'stale';
}

/**
 * One DM bubble, plus a Translate menu for any supported language.
 *
 * A stored translation for the viewer's language is shown. Asking for another
 * language calls translateMessagePart and is remembered per thread in
 * localStorage — only when that translation is already cached, so opening a
 * thread never spends a model call on old bubbles.
 *
 * Twin of town-square/src/components/messaging/TranslatableMessageText.tsx — edit both.
 */
export function TranslatableMessageText({
  content,
  sourceLocale,
  translations,
  isSelf,
  messagePartId,
  threadId,
}: TranslatableMessageTextProps) {
  const { t, i18n } = useTranslation('community');
  const viewerLocale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const [showOriginal, setShowOriginal] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [extra, setExtra] = useState<TranslationRow[]>([]);
  const phaseRef = useRef(initialDmTranslateClick);
  const [phase, setPhase] = useState(initialDmTranslateClick);
  const requestedRef = useRef<{ locale: string; generation: number } | null>(null);
  const [translate] = useMutation(TRANSLATE_MESSAGE_PART);
  // While the flag is off every pick fails with TRANSLATION_UNAVAILABLE, so hide the menu.
  const { enabled: canTranslate } = useFeatureFlag('dm_translation');
  const dispatch = useCallback((event: DmTranslateClickEvent) => {
    const next = reduceDmTranslateClick(phaseRef.current, event);
    phaseRef.current = next;
    setPhase(next);
    return next;
  }, []);

  const rows = [...(translations ?? []), ...extra];
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const readyKey = rows
    .filter(isReady)
    .map((row) => row.locale)
    .join(',');

  useEffect(() => {
    const saved = storedChoice(threadId);
    if (!saved || !isLocale(saved)) return;
    if (rows.some((row) => row.locale === saved && isReady(row))) setChosen(saved);
  }, [threadId, readyKey]);

  // A handoff returns pending and the row shows up later on the part. Leave the
  // menu as soon as that row is here, including when the owning request is still open.
  useEffect(() => {
    const requested = requestedRef.current;
    if (!requested) return;
    const phaseNow = phaseRef.current;
    if (phaseNow.generation !== requested.generation) {
      requestedRef.current = null;
      return;
    }
    if (!phaseNow.busy && phaseNow.notice !== 'translating') {
      requestedRef.current = null;
      return;
    }
    const ready = rowsRef.current.some((row) => row.locale === requested.locale && isReady(row));
    if (!ready) return;
    requestedRef.current = null;
    dispatch({ type: 'translation-arrived', generation: requested.generation });
    setChosen(requested.locale);
    setShowOriginal(false);
    rememberChoice(threadId, requested.locale);
  }, [readyKey, threadId, dispatch]);

  const resolved = resolveTranslation(
    { content, sourceLocale: sourceLocale ?? undefined, translations: rows },
    viewerLocale
  );
  const chosenRow = chosen ? rows.find((row) => row.locale === chosen && isReady(row)) : undefined;
  const showingChosen = Boolean(chosenRow) && !showOriginal;
  const showingAuto = resolved.isTranslated && !showOriginal && !showingChosen;
  const displayContent = showingChosen
    ? chosenRow!.content
    : ((showOriginal ? resolved.original.content : resolved.content) ?? content);
  const showingTranslation = showingChosen || showingAuto;
  const body = showingTranslation
    ? pinDmTranslationLinks(displayContent, resolved.original.content ?? content)
    : displayContent;
  const linkVariant = isSelf ? 'onDark' : 'default';
  const source = (sourceLocale && isLocale(sourceLocale) ? sourceLocale : null) as Locale | null;
  const menuLocales = SUPPORTED_LOCALES.filter((locale) => locale !== source);
  const menuPending = dmTranslateMenuPending(phase);
  const failureNotice =
    phase.notice === 'busy'
      ? t('translation.busy')
      : phase.notice === 'unavailable'
        ? t('translation.unavailable')
        : null;

  const choose = async (locale: Locale) => {
    const existing = rows.find((row) => row.locale === locale && isReady(row));
    if (existing) {
      setChosen(locale);
      setShowOriginal(false);
      rememberChoice(threadId, locale);
      return;
    }
    if (!messagePartId) return;
    const started = dispatch({ type: 'start' });
    requestedRef.current = { locale, generation: started.generation };
    try {
      const { data } = await translate({ variables: { messagePartId, locale } });
      const result = data?.translateMessagePart as TranslationRow | undefined;
      const event = dmTranslateClickEventForResult(started.generation, result?.status);
      const next = dispatch(event);
      if (event.type === 'ready' && result) {
        setExtra((current) => (current.some((row) => row.locale === result.locale) ? current : [...current, result]));
        setChosen(locale);
        setShowOriginal(false);
        rememberChoice(threadId, locale);
      } else if (event.type === 'miss' && !next.sawReady) {
        setShowOriginal(true);
        setChosen(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      dispatch({
        type: 'error',
        generation: started.generation,
        notice: message.toLowerCase().includes('busy') ? 'busy' : 'unavailable',
      });
    }
  };

  return (
    <>
      {(showingTranslation || showOriginal) && resolved.isTranslated && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
          <span>
            {showOriginal
              ? t('translation.showing-original')
              : t('translation.translated-from', { source: resolved.sourceDisplayName ?? '' })}
          </span>
        </div>
      )}
      <div
        className={`text-sm p-2.5 rounded-lg whitespace-pre-wrap break-words ${
          isSelf ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'
        }`}
      >
        {showingTranslation ? linkifyPlainUrls(body, { linkVariant }) : parseMessageContent(body, { linkVariant })}
      </div>
      {canTranslate && messagePartId && (
        <details className="mt-1 text-xs text-gray-400">
          <summary className="cursor-pointer hover:text-gray-600">
            {menuPending ? t('translation.translating') : t('translation.translate')}
          </summary>
          <div className="mt-1 flex flex-wrap gap-2">
            {menuLocales.map((locale) => (
              <button
                key={locale}
                type="button"
                className="hover:underline"
                onClick={() => void choose(locale)}
                disabled={phase.busy}
              >
                {LOCALE_DISPLAY_NAMES[locale]}
              </button>
            ))}
            <button type="button" className="hover:underline" onClick={() => setShowOriginal(true)}>
              {t('translation.show-original')}
            </button>
          </div>
        </details>
      )}
      {failureNotice && <p className="text-xs text-gray-400 mt-1">{failureNotice}</p>}
    </>
  );
}
